import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Link,
  MenuItem,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Editor } from '@tinymce/tinymce-react';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import FilterIcon from '@material-ui/icons/FilterList';
import CardTitle from '../../common/card/CardTitle';

import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import SplitPane, { Pane } from '../../common/SplitPane';
import { auditElements } from './Audit.data';
import { AuditElement } from './Audit.interface';
import AuditControlElements from './AuditControlElements';
import environment from '../../environments';

const Styled = {
  PageTemplate: styled(PageTemplate)`
    .page-template-dashboard {
      padding: 0;
      background: #fff;
    }
    .padded-content {
      padding: 20px 30px 0 30px;
    }
  `,
  AuditListPane: styled(Pane)`
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: auto;
    .control-table {
      white-space: normal;
      td {
        vertical-align: top;
      }
    }
    td.answer {
      width: 80px;
    }
    td.comment {
      width: 30%;
      .comment-truncate {
        display: -webkit-box;
        overflow: hidden;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }
    }
    td.loading {
      width: 30%;
    }
    .depth-1 {
      padding-left: 32px !important;
    }
    .depth-2 {
      padding-left: ${32 * 2}px !important;
    }
    .depth-3 {
      padding-left: ${32 * 3}px !important ;
    }
  `,
  ControlDetailPane: styled(SplitPane)`
    padding: 12px;
    background: #f4f4f4;
    flex-direction: column;
    overflow: auto;
    //transition: 150ms ease-out;
  `,
  Box: styled(Box)`
    display: flex;
    height: 100%;
    color: #757575;
  `
};

const getAllControls = (
  elements: AuditElement[],
  controlElements: AuditElement[] = []
) => {
  for (let element of elements) {
    if (element.type === 'control') {
      controlElements.push(element);
    } else if (element.type === 'section' && element.elements) {
      getAllControls(element.elements, controlElements);
    }
  }
  return controlElements;
};

const AuditPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFileImported, setIsFileImported] = useState(false);
  const [elements, setElements] = useState<AuditElement[] | null>(null);
  const [isAllLoaded, setIsAllLoaded] = useState(false);
  const [selectedElement, setSelectedElement] = useState<
    AuditElement | undefined
  >((auditElements[0]?.elements[0] as any)?.elements[0] || undefined);
  const controlCount = useMemo(() => {
    return elements ? getAllControls(elements).length : 0;
  }, [elements]);

  let controlElements: AuditElement[] = [];
  const [elementCounter, setElementCounter] = useState<number>(0);
  const [controlElementsState, setControlElementsState] = useState<
    AuditElement[] | null
  >(null);

  const handleFile = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsFileImported(true);
    setElements(auditElements);
    loadAuditSheet();
  };

  const getControlElements = (
    elements: AuditElement[],
    controlElements?: AuditElement[]
  ): AuditElement[] => {
    if (!controlElements) controlElements = [];
    for (let element of elements) {
      if (element.type === 'control') {
        controlElements.push(element);
      } else if (element.type === 'section' && element.elements) {
        getControlElements(element.elements, controlElements);
      }
    }
    return controlElements;
  };

  useEffect(() => {
    if (controlElementsState) {
      loadNextAuditSheetElement(controlElementsState, elementCounter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlElementsState]);

  const loadAuditSheet = () => {
    controlElements = getControlElements(auditElements);
    setControlElementsState(controlElements);
  };

  const loadNextAuditSheetElement = (
    controlElements: AuditElement[],
    index: number
  ) => {
    if (controlElements.length <= index) {
      setIsAllLoaded(true);
      return;
    }
    let element = controlElements[index];
    element.isLoading = false;
    setTimeout(() => {
      setElementCounter(elementCounter + 1);
      setControlElementsState([...controlElements]);
    }, 100 + Math.floor(Math.random() * 300) + 1);
  };

  return (
    <Styled.PageTemplate>
      <PageTemplateHeader>
        <h2>Audit Management</h2>
        <PageTemplateHeaderActions flex>
          <input
            hidden
            type='file'
            ref={fileInputRef}
            onChange={event => handleFile(event?.target.files)}
          />
          {isFileImported ? (
            <>
              <Button color='primary' variant='contained' onClick={() => {}}>
                Save
              </Button>
              <Button color='primary' variant='contained' onClick={() => {}}>
                Export
              </Button>
            </>
          ) : (
            <Button
              color='primary'
              variant='contained'
              onClick={() => fileInputRef.current?.click()}
            >
              Import
            </Button>
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        <SplitPane primary='next'>
          <Styled.AuditListPane flex className='audit-table'>
            <Table className='audit-header'>
              <TableHead>
                <TableRow>
                  <TableCell className='control'>
                    Controls {isFileImported && `(Total: ${controlCount})`}
                  </TableCell>
                  <TableCell className='answer'>Answer</TableCell>
                  <TableCell className='comment'>Comments</TableCell>
                </TableRow>
              </TableHead>
            </Table>
            {isAllLoaded && (
              <Alert severity='error'>7 Unidentified Controls</Alert>
            )}
            {isFileImported && elements ? (
              <AuditControlElements
                elements={elements}
                onToggleElement={element => {
                  element.collapsed = !element.collapsed;
                  setElements([...auditElements]);
                }}
                onSelectElement={element => {
                  if (element === selectedElement) {
                    setSelectedElement(undefined);
                  } else {
                    setSelectedElement(element);
                  }
                }}
              />
            ) : (
              <Styled.Box>
                <Box m='auto' style={{ textAlign: 'center' }}>
                  <Box m='auto'>
                    <FilterIcon />
                  </Box>
                  Upload an audit excel document containing audit control to
                  validate
                </Box>
              </Styled.Box>
            )}
          </Styled.AuditListPane>
          {selectedElement && isFileImported && (
            <Styled.ControlDetailPane
              width='30%'
              className='control-detail-pane'
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card>
                    <CardTitle>Control</CardTitle>
                    <CardContent>{selectedElement.control}</CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <Box mb={2}>
                      <CardTitle>
                        Answer
                        <TextField
                          style={{ marginLeft: 20, width: 100 }}
                          select
                          size='small'
                          // variant='outlined'
                          value={selectedElement.answer}
                        >
                          <MenuItem value='Yes'>Yes</MenuItem>
                          <MenuItem value='No'>No</MenuItem>
                        </TextField>
                      </CardTitle>
                    </Box>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <CardTitle>Comment</CardTitle>
                    <CardContent>
                      <Editor
                        apiKey={environment.tinyApiKey}
                        value={selectedElement.comment || ''}
                        init={{
                          height: 240,
                          menubar: false,
                          contextmenu: false,
                          browser_spellcheck: true,
                          plugins: [
                            'advlist autolink lists link charmap print preview anchor',
                            'searchreplace visualblocks code',
                            'insertdatetime media table paste code help autoresize'
                          ],
                          toolbar:
                            'formatselect | bold italic backcolor | link bullist numlist outdent indent | removeformat | help | code'
                        }}
                        onEditorChange={content => {
                          selectedElement.comment = content;
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                {selectedElement.elements?.map(element => (
                  <Grid item xs={12}>
                    <Card>
                      <CardTitle>{element.title}</CardTitle>
                      <CardContent
                        dangerouslySetInnerHTML={{
                          __html: element.comment || ''
                        }}
                      />
                      <Divider />
                      {element.links && element.links.length > 0 && (
                        <CardActions>
                          {element.links?.map(link => (
                            <Button
                              component={Link}
                              href={link.src}
                              target='_blank'
                              size='small'
                              color='primary'
                            >
                              {link.label}
                            </Button>
                          ))}
                        </CardActions>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Styled.ControlDetailPane>
          )}
        </SplitPane>
      </PageTemplateDashboard>
    </Styled.PageTemplate>
  );
};

export default AuditPage;
